/**
 * Validation des fichiers choisis par un invité avant dépôt dans l'album.
 */
import type { MediaKind, RejectionReason, ValidationResult } from "@/types/album";

import { formatBytes } from "@/lib/format";

export const MAX_IMAGE_BYTES = 25 * 1024 * 1024;
export const MAX_VIDEO_BYTES = 200 * 1024 * 1024;
export const MAX_BATCH = 20;

/** Extensions reconnues quand le système ne renseigne aucun type MIME. */
const IMAGE_EXTENSIONS = /\.(jpe?g|png|gif|webp|avif|bmp|heic|heif)$/i;
const VIDEO_EXTENSIONS = /\.(mp4|m4v|mov|webm|avi|mkv|3gp)$/i;

/** Formats de la famille HEIF, natifs des iPhone. */
const HEIC_MIME_TYPES = new Set([
  "image/heic",
  "image/heif",
  "image/heic-sequence",
  "image/heif-sequence",
]);

/**
 * Détermine la nature d'un fichier.
 *
 * Le type MIME fait foi, mais certains sélecteurs de fichiers — notamment sur
 * Android et pour les formats HEIF — le laissent vide. On retombe alors sur
 * l'extension plutôt que de rejeter une photo parfaitement valide.
 */
export function detectKind(file: File): MediaKind | null {
  // Le SVG n'est pas une photo de mariage : c'est un document susceptible de
  // porter du script. Rien ne l'exécute aujourd'hui, mais l'accepter ferait
  // reposer cette garantie sur les composants d'affichage plutôt que sur la
  // validation, qui est le bon endroit pour la tenir.
  if (file.type === "image/svg+xml") return null;
  if (file.type.startsWith("image/")) return "image";
  if (file.type.startsWith("video/")) return "video";
  if (file.type === "") {
    if (IMAGE_EXTENSIONS.test(file.name)) return "image";
    if (VIDEO_EXTENSIONS.test(file.name)) return "video";
  }
  return null;
}

/**
 * Repère les photos au format HEIC/HEIF, que seul Safari sait afficher.
 *
 * Le fichier reste accepté : il est valide, conservé intact et téléchargeable.
 * Mais son aperçu échouera hors Safari, et l'invité mérite d'en connaître la
 * raison exacte plutôt que de voir une vignette grise sans explication.
 */
export function isLikelyHeic(file: File): boolean {
  return HEIC_MIME_TYPES.has(file.type.toLowerCase()) || /\.(heic|heif)$/i.test(file.name);
}

/** Signature d'identité d'un fichier, pour repérer les doublons. */
function fileSignature(file: File): string {
  return `${file.name}::${file.size}::${file.lastModified}`;
}

/**
 * Valide une sélection de fichiers face aux fichiers déjà retenus.
 * L'ordre des motifs de rejet est significatif : type, doublon, taille, plafond de lot.
 */
export function validateFiles(files: File[], alreadySelected: File[]): ValidationResult {
  const accepted: File[] = [];
  const rejected: ValidationResult["rejected"] = [];
  const seenSignatures = new Set(alreadySelected.map(fileSignature));

  for (const file of files) {
    const kind = detectKind(file);
    if (kind === null) {
      rejected.push({ file, reason: "type" });
      continue;
    }

    const signature = fileSignature(file);
    if (seenSignatures.has(signature)) {
      rejected.push({ file, reason: "duplicate" });
      continue;
    }

    const maxBytes = kind === "video" ? MAX_VIDEO_BYTES : MAX_IMAGE_BYTES;
    if (file.size > maxBytes) {
      rejected.push({ file, reason: "size" });
      continue;
    }

    if (alreadySelected.length + accepted.length >= MAX_BATCH) {
      rejected.push({ file, reason: "batch-limit" });
      continue;
    }

    seenSignatures.add(signature);
    accepted.push(file);
  }

  return { accepted, rejected };
}

/** Message français prêt à afficher pour expliquer le rejet d'un fichier. */
export function describeRejection(reason: RejectionReason, file: File): string {
  switch (reason) {
    case "type":
      return `${file.name} n'est ni une photo ni une vidéo.`;
    case "duplicate":
      return `${file.name} est déjà dans votre sélection.`;
    case "size": {
      const kind = detectKind(file);
      const maxBytes = kind === "video" ? MAX_VIDEO_BYTES : MAX_IMAGE_BYTES;
      // La taille réelle est indiquée : sans elle, l'invité ignore de combien
      // il dépasse et ne sait pas s'il doit renoncer ou simplement compresser.
      return `${file.name} pèse ${formatBytes(file.size)}, au-delà de la limite de ${formatBytes(maxBytes)}.`;
    }
    case "batch-limit":
      return `Seuls ${MAX_BATCH} fichiers peuvent être envoyés d'un coup.`;
  }
}
